// src/components/employee/EmployeeSidebar.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
    Home, User, Clock, Calendar, DollarSign,
    FileText, Users, ChevronLeft, ChevronRight, UserPlus, Lock, TrendingUp, Target, Briefcase, LogOut
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useEmployees } from '../../contexts/EmployeeContext';
import { isInExitProcess, getDaysRemainingInNoticePeriod } from '../../utils/exitStatusHelper';
import logo from '../../oakhr-logo-full.svg';
import logo2 from '../../oakhr-tree.svg';

const EmployeeSidebar = ({ isOpen, onToggle, user }) => {
    const { theme } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const { onboardingData } = useOnboarding();
    const { employees } = useEmployees();

    // Get current employee from context
    const currentEmployee = employees.find(emp => emp.email === user?.email) || user;

    // Check if user is onboarded (admin approved)
    const isOnboarded = user?.id && onboardingData ? onboardingData[user.id]?.adminReviewed : false;

    // Check if employee is in exit process
    const inExitProcess = isInExitProcess(currentEmployee);
    const daysRemaining = getDaysRemainingInNoticePeriod(currentEmployee);

    // Hover flyout state
    const [hoveredItem, setHoveredItem] = useState(null);
    const [flyoutPos, setFlyoutPos] = useState({ top: 0, left: 0 });
    const hoverTimeoutRef = useRef(null);

    // Debug log
    useEffect(() => {
        if (user?.id && onboardingData) {
            // debug logging removed
        }
    }, [user?.id, user?.first_name, user?.last_name, isOnboarded, onboardingData]);

    const menuItems = [
        { path: '/employee/dashboard', icon: Home, label: 'Dashboard', description: 'Overview and insights', alwaysEnabled: true, tourClass: 'tour-employee-dashboard' },
        { path: '/employee/profile', icon: User, label: 'My Profile', description: 'Personal profile and settings', alwaysEnabled: true, tourClass: 'tour-employee-profile' },
        { path: '/employee/onboarding', icon: UserPlus, label: 'Onboarding', description: 'Onboarding tasks', badge: !isOnboarded, alwaysEnabled: true, tourClass: 'tour-employee-onboarding' },
        { path: '/employee/attendance', icon: Clock, label: 'Attendance', description: 'Time tracking', alwaysEnabled: true, tourClass: 'tour-employee-attendance' },
        { path: '/employee/leaves', icon: Calendar, label: 'Leaves', description: 'Leave requests', alwaysEnabled: true, tourClass: 'tour-employee-leaves' },
        { path: '/employee/performance', icon: TrendingUp, label: 'Performance', description: 'Self reviews & feedback', alwaysEnabled: true, tourClass: 'tour-employee-performance', subItems: [
            { tab: 'reviews', label: 'Reviews' },
            { tab: 'analytics', label: 'Analytics' },
        ]},
        { path: '/employee/goals', icon: Target, label: 'Goals', description: 'Track your goals', alwaysEnabled: true, tourClass: 'tour-employee-goals' },
        { path: '/employee/team-tasks', icon: Briefcase, label: 'Team Tasks', description: 'Tasks assigned to you', alwaysEnabled: true, tourClass: 'tour-employee-team-tasks' },
        { path: '/employee/payroll', icon: DollarSign, label: 'Compensation', description: 'Salary and Tax info', alwaysEnabled: true, tourClass: 'tour-employee-payroll', subItems: [
            { tab: 'PAY PACKAGE', label: 'Pay Package' },
            { tab: 'PAY SLIPS', label: 'Pay Slips' },
            { tab: 'TAX (TDS)', label: 'Tax (TDS)' },
        ]},
        { path: '/employee/documents', icon: FileText, label: 'Documents', description: 'Personal documents', alwaysEnabled: true, tourClass: 'tour-employee-documents', subItems: [
            { tab: 'received', label: 'Received' },
            { tab: 'uploaded', label: 'Uploaded' },
        ]},
    ];

    if (user?.is_manager) {
        menuItems.push({ path: '/employee/team', icon: Users, label: 'My Team', description: 'Team overview', subItems: [
            { tab: 'members', label: 'Members' },
            { tab: 'attendance', label: 'Attendance' },
            { tab: 'leaves', label: 'Leaves' },
        ]});
    }

    const isActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const currentTab = new URLSearchParams(location.search).get('tab');

    const handleMouseEnter = useCallback((path, e) => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        const rect = e.currentTarget.getBoundingClientRect();
        setFlyoutPos({ top: rect.top, left: rect.right + 4 });
        setHoveredItem(path);
    }, []);

    const handleMouseLeave = useCallback(() => {
        hoverTimeoutRef.current = setTimeout(() => {
            setHoveredItem(null);
        }, 150);
    }, []);

    const handleSubItemClick = useCallback((path, tab) => {
        setHoveredItem(null);
        navigate(`${path}?tab=${tab}`);
    }, [navigate]);

    const renderFlyout = (item) => {
        const hasSubItems = item.subItems && item.subItems.length > 0;
        const isHovered = hoveredItem === item.path;
        if (!hasSubItems || !isHovered) return null;

        return (
            <div
                className="fixed z-[9999]"
                style={{ top: flyoutPos.top - 8, left: flyoutPos.left }}
                onMouseEnter={() => { if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current); }}
                onMouseLeave={handleMouseLeave}
            >
                <div className={`rounded-xl shadow-xl border min-w-[190px] overflow-hidden divide-y ${
                    theme === 'dark'
                        ? 'bg-gray-800 border-gray-700 shadow-black/30 divide-gray-700'
                        : 'bg-white border-gray-200 shadow-gray-200/50 divide-gray-100'
                }`}>
                    {item.subItems.map((sub) => {
                        const isSubActive = isActive(item.path) && currentTab === sub.tab;
                        return (
                            <button
                                key={sub.tab}
                                onClick={() => handleSubItemClick(item.path, sub.tab)}
                                className={`w-full text-left px-3 py-1.5 text-xs font-medium transition-colors flex items-center justify-between ${
                                    isSubActive
                                        ? theme === 'dark'
                                            ? 'bg-blue-900/40 text-blue-300'
                                            : 'bg-blue-50 text-blue-700'
                                        : theme === 'dark'
                                            ? 'text-gray-300 hover:bg-gray-700/70 hover:text-white'
                                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                            >
                                <span className="flex-1">{sub.label}</span>
                                <ChevronRight className="w-3 h-3 flex-shrink-0 opacity-40" />
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <aside className={`${isOpen ? 'w-64' : 'w-20'} h-screen border-r flex flex-col transition-all duration-300 ${
            theme === 'dark'
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
        }`}>
            {/* Logo */}
            <div className={`p-6 border-b flex items-center justify-between ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
                {isOpen ? (
                    <div className="flex flex-col items-center justify-center flex-1">
                        <img src={logo} alt="Logo" className="h-30 w-auto object-contain" />
                        <p className={`mt-2 text-xs ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                            Powered by KONAV Technologies Pvt. Ltd.
                        </p>
                    </div>
                ) : (
                    <img src={logo2} alt="Logo" className="h-16 w-auto object-contain mx-auto" />
                )}
                <button
                    onClick={onToggle}
                    className={`p-2 rounded-lg transition-colors ${
                        theme === 'dark'
                            ? 'text-gray-300 hover:bg-gray-700'
                            : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    {isOpen ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto p-4 space-y-2">
                {menuItems.map((item) => {
                    const IconComponent = item.icon;
                    const active = isActive(item.path);
                    const isLocked = item.locked && !item.alwaysEnabled;
                    const hasSubItems = item.subItems && item.subItems.length > 0;
                    const isHovered = hoveredItem === item.path;

                    if (isLocked) {
                        return (
                            <div
                                key={item.path}
                                className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg cursor-not-allowed ${
                                    theme === 'dark'
                                        ? 'text-gray-600 bg-gray-800'
                                        : 'text-gray-400 bg-gray-100'
                                }`}
                                title={isOpen ? 'Complete onboarding to unlock' : item.description}
                            >
                                <IconComponent className="w-5 h-5 flex-shrink-0" />
                                {isOpen && (
                                    <>
                                        <span className="font-medium">{item.label}</span>
                                        <Lock className="w-4 h-4 ml-auto" />
                                    </>
                                )}
                            </div>
                        );
                    }

                    return (
                        <div
                            key={item.path}
                            className="relative"
                            onMouseEnter={(e) => hasSubItems && handleMouseEnter(item.path, e)}
                            onMouseLeave={handleMouseLeave}
                        >
                            <NavLink
                                to={item.path}
                                className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors group ${item.tourClass || ''} ${
                                    active
                                        ? theme === 'dark'
                                            ? 'bg-blue-900 text-blue-100'
                                            : 'bg-blue-50 text-blue-700'
                                        : theme === 'dark'
                                            ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                                }`}
                                title={isOpen ? '' : item.description}
                            >
                                <IconComponent className="w-5 h-5 flex-shrink-0" />
                                {isOpen && (
                                    <>
                                        <span className="font-medium flex-1 text-left">{item.label}</span>
                                        {item.badge && (
                                            <span className="px-2 py-0.5 text-xs bg-orange-500 text-white rounded-full">
                                                New
                                            </span>
                                        )}
                                        {hasSubItems && (
                                            <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${
                                                isHovered ? 'translate-x-0.5' : ''
                                            } ${
                                                active
                                                    ? theme === 'dark' ? 'text-blue-300' : 'text-blue-500'
                                                    : theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                            }`} />
                                        )}
                                    </>
                                )}
                            </NavLink>

                            {/* Flyout sub-menu */}
                            {renderFlyout(item)}
                        </div>
                    );
                })}
            </nav>

            {/* Exit Status Indicator */}
            {inExitProcess && (
                <div className={`p-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                        theme === 'dark' ? 'bg-amber-900/30' : 'bg-amber-50'
                    }`}>
                        <LogOut className={`w-5 h-5 flex-shrink-0 ${
                            theme === 'dark' ? 'text-amber-400' : 'text-amber-600'
                        }`} />
                        {isOpen && (
                            <div className="flex-1 min-w-0">
                                <p className={`text-xs font-semibold ${
                                    theme === 'dark' ? 'text-amber-300' : 'text-amber-700'
                                }`}>
                                    Notice Period
                                </p>
                                <p className={`text-xs truncate ${
                                    theme === 'dark' ? 'text-amber-400' : 'text-amber-600'
                                }`}>
                                    {daysRemaining !== null
                                        ? daysRemaining > 0
                                            ? `${daysRemaining} days left`
                                            : daysRemaining === 0
                                                ? 'Last day!'
                                                : `Passed ${Math.abs(daysRemaining)}d ago`
                                        : 'In progress'
                                    }
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </aside>
    );
};
export default EmployeeSidebar;
